/**
 * L'écran de partie : le zinc, les dés, et tout ce qu'il faut savoir pour jouer.
 *
 * ## Ce que ce composant ne fait pas
 *
 * Il ne décide de rien. Le résultat des dés vient du moteur (`appliquer`), le geste
 * de la scène, les décisions des adversaires du pilote d'IA, et ce qu'il faut
 * afficher des sélecteurs de `tableau.ts`. Ce fichier n'est que du branchement — et
 * c'est la raison pour laquelle la logique a été sortie dans des modules purs : elle
 * y est testable, ici elle ne le serait pas.
 *
 * ## Les IA jouent une action à la fois
 *
 * `jouerJusquAuJoueur` aurait fait avancer la partie d'un bloc jusqu'au tour du
 * joueur — et personne n'aurait rien vu. Chaque action d'IA est donc jouée seule,
 * après son temps de réflexion (§ 4.2), et le jet qu'elle produit est mis en scène
 * avant la suivante.
 *
 * ⚠️ Aucun `agir()` dans un updater de `setState`. React rappelle volontairement les
 * updaters deux fois en mode strict, et `agir` consomme le tireur : on tirerait deux
 * jets pour en garder un. C'est le bug déjà payé au lot 3, sous une autre forme.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  conseiller, terminee, tireurCrypto,
  type Action, type Partie as EtatPartie, type Statistiques, type Tables,
} from "@zinc421/shared";

import { Scene } from "../scene/Scene.js";
import { Annonces, tonCourant, useAnnonces } from "./BandeauAnnonces.js";
import type { DePose, Jet } from "../scene/lancer.js";
import { posesApresJet, tapisInitial } from "../scene/tapis.js";
import {
  actionIA, actionsOffertes, agir, aQuiDeJouer, desAffiches, garderOuRelacher,
  jetTermine, ouvrirSession, poseForcee, renommer, tempoAvantAction,
  type Reglages, type Session,
} from "./session.js";
import {
  passageNeuf, prendreLAppareil, suivrePassage, type EtatPassage,
} from "./passage.js";
import { useTextes } from "../i18n/contexte.js";
import { phraseDuConseil } from "./conseil.js";
import { useBruitage } from "../son/useBruitage.js";
import { useStatistiques } from "./suivi.js";
import {
  consigne, enjeuDeLaPhase, finVisible, lignesJoueurs, podium, posesTriees,
} from "./tableau.js";

interface Props {
  readonly depart: EtatPartie;
  readonly tables: Tables;
  /** « La cave », « La recave »… — affiché en tête, pour savoir où l'on en est. */
  readonly etape: string;
  /** Ce que propose le bouton de fin : la cave suivante, ou une nouvelle soirée. */
  readonly libelleSuite: string;
  /**
   * Le siège dont on tient les statistiques — la campagne, où l'appareil sert un
   * seul joueur. Absent en hot-seat : il n'y aurait personne dont les compter.
   */
  readonly monSiege?: number;
  /** Le son (et l'haptique) sont-ils actifs ? Réglage retenu par le coffre. */
  readonly son: boolean;
  readonly onSon: (actif: boolean) => void;
  /**
   * Reçoit la partie TELLE QU'ELLE S'EST TERMINÉE, et son relevé.
   *
   * Le parent ne détient que l'état de départ : la cave se joue ici, dans une session
   * locale. Lui demander d'enchaîner sans lui rendre le résultat revient à lui faire
   * clôturer une partie qui, pour lui, n'a pas encore commencé. Le relevé suit le
   * même chemin, et pour la même raison : il ne se reconstruit pas depuis l'état
   * final, il s'accumule en cours de route.
   */
  readonly onSuite: (partieFinie: EtatPartie, releve: Statistiques) => void;
  /**
   * Remonte l'état courant à chaque transition, pour que le parent puisse le
   * sauvegarder (§ 5.4).
   *
   * Le parent ne détient que l'état de DÉPART de la cave : sans ce canal, une partie
   * quittée en plein rampeau ne pourrait être reprise qu'à sa première manche. Le
   * parent est censé le ranger dans une ref — c'est appelé à chaque révision.
   */
  readonly onEtat?: (partie: EtatPartie, releve: Statistiques) => void;
  /**
   * Un joueur a été renommé en cours de partie.
   *
   * Le parent doit le reporter dans la TABLE de la soirée, sinon le nom retomberait
   * à sa valeur d'origine à la cave suivante — c'est la table, et non la cave, qui
   * porte l'identité des joueurs d'un bout à l'autre.
   */
  readonly onRenomme?: (siege: number, nom: string) => void;
  readonly onQuitter: () => void;
}

export function Partie({
  depart, tables, etape, libelleSuite, monSiege, son, onSon, onSuite, onEtat,
  onRenomme, onQuitter,
}: Props) {
  const textes = useTextes();
  const [session, setSession] = useState<Session>(() => ouvrirSession(depart));
  const [poses, setPoses] = useState<readonly DePose[]>(
    () => tapisInitial().map((d) => d.pose));
  /** Incrémenté à chaque jet posé : c'est ce qui déclenche le regard sur le résultat. */
  const [regard, setRegard] = useState(0);
  /** Ce qui a été refusé. Affiché plutôt qu'avalé : un refus muet est un bug muet. */
  const [probleme, setProbleme] = useState<string | null>(null);
  /** Qui tient l'appareil, en hot-seat. Sans objet quand un seul humain joue. */
  const [passage, setPassage] = useState<EtatPassage>(passageNeuf);
  /** Le conseil demandé, affiché jusqu'au coup suivant. */
  const [conseilVu, setConseilVu] = useState(false);
  /** Le siège en cours de renommage, ou `null`. */
  const [renomme, setRenomme] = useState<{ siege: number; nom: string } | null>(null);

  const reglages = useMemo<Reglages>(() => ({
    tables,
    tireur: tireurCrypto,
    alea: Math.random,
    // Cosmétique : elle ne décide que du geste. Au lot 5, elle viendra du serveur
    // avec le résultat, sinon deux joueurs verraient deux lancers différents.
    graine: () => Math.floor(Math.random() * 0x7fffffff),
  }), [tables]);

  // On tutoie quand l'appareil sert UN seul joueur. En hot-seat, l'écran passe de
  // main en main : « tu ramasses » désignerait alors n'importe qui.
  const humains = session.partie.joueurs.filter((j) => !j.estIA);
  const monPointDeVue = humains.length === 1 ? humains[0]!.siege : undefined;
  const annonces = useAnnonces(session.partie, monPointDeVue);
  const releve = useStatistiques(session.partie, monSiege);
  // ⚠️ Déclaré AVANT les rappels qui s'en servent : `bruitage` figure dans le tableau
  // de dépendances de `surDeClique`, lequel est évalué au rendu. Le déclarer plus bas
  // serait une zone morte temporelle, et l'écran planterait au premier affichage.
  const bruitage = useBruitage(session.partie, monPointDeVue, son);

  /** Joue une action, en laissant voir ce qui coince plutôt qu'en l'ignorant. */
  const jouer = useCallback((action: Action, courante: Session) => {
    try {
      setSession(agir(courante, action, reglages));
      setProbleme(null);
    } catch (erreur) {
      setProbleme(erreur instanceof Error ? erreur.message : String(erreur));
    }
  }, [reglages]);

  // Les adversaires, un coup à la fois. L'effet se redéclenche à chaque nouvel état :
  // c'est la boucle de jeu, et elle s'arrête d'elle-même dès que la main revient.
  useEffect(() => {
    if (session.demande !== null) return;      // un jet roule : la scène a la main
    const tour = aQuiDeJouer(session.partie);
    if (!tour?.estIA) return;

    const attente = tempoAvantAction(session, Math.random()) * 1000;
    const minuteur = setTimeout(() => {
      const action = actionIA(session, reglages);
      if (action) jouer(action, session);
    }, attente);
    return () => { clearTimeout(minuteur); };
  }, [session, reglages, jouer]);

  /**
   * Le jet est joué : les dés sont posés là où la physique les a laissés.
   *
   * Les deux mises à jour passent par des updaters, et ceux-ci sont **purs** — elles
   * viennent de `useFrame`, hors du cycle de rendu, où lire un état capturé donnerait
   * une valeur périmée.
   */
  const surJetJoue = useCallback((indices: readonly number[], jet: Jet) => {
    setPoses((precedentes) => posesApresJet(precedentes, indices, jet));
    setSession(jetTermine);
    // Les dés viennent de s'immobiliser : c'est LE moment du résultat, et donc
    // celui du regard. Un compteur, et non un booléen : deux jets d'affilée
    // doivent relancer le mouvement, pas l'annuler.
    setRegard((n) => n + 1);
  }, []);

  const surContextePerdu = useCallback(() => {
    // Un jet dont l'animation ne se terminera jamais bloquerait la partie : on le
    // considère joué. L'état, lui, n'a rien perdu — il vit hors de la scène.
    setSession(jetTermine);
  }, []);

  const surDeClique = useCallback((index: number) => {
    // Le rideau bloque aussi les dés de la SCÈNE. Les boutons du HUD, eux, sont
    // désactivés par `offertes` — mais on peut toucher un dé en 3D, et un rideau
    // qu'on contourne d'un doigt ne marque plus rien.
    if (passage.attendu !== null) return;
    const action = garderOuRelacher(session, index);
    if (!action) return;
    // Garder ou relâcher ne sonne pas pareil : c'est le seul retour immédiat qu'a le
    // joueur sur un geste dont la 3D, elle, ne montre qu'une nuance de matière.
    bruitage.surGarde(!(session.partie.manche?.enCours?.des[index]?.garde ?? false));
    jouer(action, session);
  }, [session, jouer, bruitage, passage.attendu]);

  // Le rideau de passage suit la session : c'est un effet, et non un calcul en plein
  // rendu, parce qu'il porte une mémoire — qui tenait l'appareil au coup d'avant.
  useEffect(() => {
    setPassage((avant) => suivrePassage(avant, session, humains.length));
  }, [session, humains.length]);

  // Le parent suit l'état pour pouvoir le sauvegarder. Un effet, et non un appel en
  // plein rendu : prévenir un parent pendant qu'on rend est exactement le défaut qui
  // a fait perdre le déblocage de campagne.
  useEffect(() => {
    onEtat?.(session.partie, releve);
  }, [session.partie, releve, onEtat]);

  /**
   * La main se pose toute seule quand il n'y a plus rien à décider.
   *
   * Au dernier lancer, « Je m'arrête » ne décide de rien — il n'y a pas
   * d'alternative — et tant que personne ne clique, toute la table attend. Olivier
   * l'a signalé comme un blocage, et c'en est un.
   *
   * ⚠️ Avec un temps d'arrêt, et il n'est pas cosmétique : le joueur doit VOIR sa
   * dernière main avant qu'elle parte. Poser à l'image même où les dés s'immobilisent
   * donnerait l'impression d'un coup escamoté.
   */
  useEffect(() => {
    if (!poseForcee(session)) return;
    const minuteur = setTimeout(() => { jouer({ type: "ARRETER" }, session); }, 900);
    return () => { clearTimeout(minuteur); };
  }, [session, jouer]);

  // Le conseil s'efface au coup suivant : il portait sur une main qui n'est plus là,
  // et le laisser affiché en ferait un mensonge une seconde plus tard.
  useEffect(() => { setConseilVu(false); }, [session.partie.revision]);

  /**
   * Ce que le taulier ferait — la politique du niveau 8, lue dans les mêmes tables
   * que l'IA (§ 12, « le coach »). Calculé à chaque état parce qu'il faut savoir s'il
   * y a quelque chose à conseiller AVANT de proposer le bouton : un bouton grisé dit
   * « il n'y a rien à dire ici », ce qui est déjà une information.
   */
  const conseil = useMemo(
    () => conseiller(session.partie, tables, monSiege),
    [session.partie, tables, monSiege]);

  // Tant que le rideau est là, l'appareil n'est à personne : aucune action offerte.
  // C'est la moitié qui compte — un rideau qu'on peut contourner au clavier ne
  // protège de rien, et le hot-seat laisserait jouer n'importe qui à la place d'un
  // autre.
  const offertes = passage.attendu === null ? actionsOffertes(session) : [];
  const des = desAffiches(session);
  const aRelancer = des.filter((d) => !d.garde).length;
  const fini = finVisible(session);
  const lignes = lignesJoueurs(session.partie, textes);
  const posees = posesTriees(session.partie, textes);

  return (
    <div className="ecran">
      <Scene
        ambiance={tonCourant(annonces)}
        des={des}
        poses={poses}
        demande={session.demande}
        onJetJoue={surJetJoue}
        onDeClique={surDeClique}
        regard={regard}
        onImpact={bruitage.surChoc}
        onContextePerdu={surContextePerdu}
      />

      <Annonces annonces={annonces} />

      <div className="bandeau">
        <p className="etape-cave">{etape}</p>
        <p className="phase">{enjeuDeLaPhase(session.partie, textes)}</p>
        <p className="masse">
          {textes.masse(textes.jetons(session.partie.masse))}
          {session.partie.manche
            && ` · ${textes.mancheNumero(session.partie.manche.numero)}`}
        </p>
        <ul className="zinc" aria-label={textes.lesJoueurs}>
          {lignes.map((ligne) => (
            <li
              key={ligne.siege}
              className={`joueur${ligne.aLaMain ? " joueur--main" : ""}`
                + `${ligne.sauve ? " joueur--sauve" : ""}`}
            >
              {/* Le nom est un bouton : à plusieurs sur un téléphone, personne ne
                saisit les noms avant de commencer, et trois « Joueur 2 » rendent le
                tableau illisible. Renommer ne touche à aucune donnée de règle. */}
            {ligne.estIA ? (
              <span className="joueur-nom">{ligne.nom}</span>
            ) : (
              <button
                type="button"
                className="joueur-nom joueur-nom--modifiable au-fil-du-texte"
                aria-label={textes.renommerJoueur(ligne.nom)}
                onClick={() => setRenomme({ siege: ligne.siege, nom: ligne.nom })}
              >
                {ligne.nom}
              </button>
            )}
              <span className="joueur-jetons">
                {ligne.sauve ? textes.sauve : textes.jetons(ligne.jetons)}
              </span>
              {ligne.pose && <span className="joueur-pose">{ligne.pose.nom}</span>}
            </li>
          ))}
        </ul>
        {posees.length > 0 && (
          <>
            {/* Le § 3.3 en fait un point de règle, pas de décor : voir ce qu'on doit
                battre vaut +4,8 points de taux de défaite (rapport § 8). */}
            <p className="titre-poses">{textes.dejaPose}</p>
            <ol className="poses">
              {posees.map((score) => (
                <li key={score.siege}>
                  <span>{score.nom}</span>
                  <span className="poses-combinaison">{score.combinaison}</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>

      <div className="hud">
        <p className="etape" role="status" aria-live="polite">
          {consigne(session, textes)}
        </p>

        {/* Les dés, en boutons : seule façon de jouer au clavier, et seule façon
            pour un lecteur d'écran de connaître l'état du tapis. */}
        <ul className="des" aria-label={textes.lesTroisDes}>
          {des.map((de, i) => (
            <li key={i}>
              <button
                type="button"
                className={`de${de.garde ? " de--garde" : ""}`}
                aria-pressed={de.garde}
                disabled={!offertes.includes("GARDER") || de.valeur === null}
                onClick={() => surDeClique(i)}
              >
                <span aria-hidden="true">{de.valeur ?? "?"}</span>
                <span className="lecteur-ecran">
                  {textes.deAria(i + 1, de.valeur === null
                    ? textes.deEnLAir
                    : (de.garde ? textes.deGarde(de.valeur) : String(de.valeur)))}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="actions">
          <button
            className="bouton"
            onClick={() => {
              // Le souffle du geste part tout de suite ; les claquements suivront
              // au rythme de la trajectoire, calculés avec elle.
              bruitage.surLancer(aRelancer);
              jouer({ type: "LANCER" }, session);
            }}
            disabled={!offertes.includes("LANCER")}
          >
            {session.partie.manche?.enCours
              ? textes.relancerDes(aRelancer)
              : textes.lancerLesDes}
          </button>
          <button
            className="bouton bouton--discret"
            onClick={() => jouer({ type: "ARRETER" }, session)}
            disabled={!offertes.includes("ARRETER")}
          >
            {textes.jeMArrete}
          </button>
          <button
            className="bouton bouton--discret"
            onClick={() => setConseilVu(true)}
            disabled={conseil === null || offertes.length === 0 || conseilVu}
            aria-label={textes.demanderConseil}
          >
            {textes.unConseil}
          </button>
        </div>

        {conseilVu && conseil && (
          <p className="conseil" role="status">{phraseDuConseil(conseil, textes)}</p>
        )}

        {probleme && <p className="alerte" role="alert">{probleme}</p>}
      </div>

      {fini && (
        <div className="fin" role="dialog" aria-label={textes.finDePartie}>
          <h2>{textes.cEstFini}</h2>
          <ol className="podium">
            {podium(session.partie).map((rang) => (
              <li key={rang.siege} className={rang.paieLaTournee ? "podium--dernier" : ""}>
                <span className="podium-rang">{rang.rang}</span>
                <span>{rang.nom}</span>
                {rang.paieLaTournee
                  && <span className="podium-note">{textes.paieSaTournee}</span>}
              </li>
            ))}
          </ol>
          <button className="bouton" onClick={() => onSuite(session.partie, releve)}>
            {libelleSuite}
          </button>
          <button className="bouton bouton--discret" onClick={onQuitter}>
            {textes.revenirAuComptoir}
          </button>
        </div>
      )}

      {renomme && (
        <div className="renommer" role="dialog" aria-label={textes.nouveauNom}>
          <input
            className="nom"
            type="text"
            value={renomme.nom}
            maxLength={16}
            autoFocus
            aria-label={textes.nouveauNom}
            onChange={(e) => setRenomme({ ...renomme, nom: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          />
          <button
            className="bouton"
            onClick={() => {
              setSession((s) => renommer(s, renomme.siege, renomme.nom));
              onRenomme?.(renomme.siege, renomme.nom);
              setRenomme(null);
            }}
          >
            {textes.valider}
          </button>
        </div>
      )}

      {passage.attendu !== null && (
        <div className="passage" role="dialog" aria-label={textes.passerLAppareilAria}>
          <p className="passage-a">{textes.passerLAppareil}</p>
          <p className="passage-nom">
            {session.partie.joueurs[passage.attendu]?.nom ?? "?"}
          </p>
          <p className="passage-enjeu">{enjeuDeLaPhase(session.partie, textes)}</p>
          <button
            className="bouton bouton--large"
            onClick={() => setPassage(prendreLAppareil)}
            autoFocus
          >
            {textes.cEstMoi}
          </button>
        </div>
      )}

      {/* Couper le son doit rester à portée de pouce : on joue au comptoir, et
          parfois dans un endroit où l'on ne peut pas faire de bruit. */}
      <button
        className="son"
        aria-pressed={son}
        aria-label={son ? textes.couperLeSon : textes.remettreLeSon}
        onClick={() => onSon(!son)}
      >
        <span aria-hidden="true">{son ? "🔊" : "🔇"}</span>
      </button>

      {/* Sortie de secours, y compris en pleine partie. */}
      {!fini && !terminee(session.partie) && (
        <button className="quitter" onClick={onQuitter}>{textes.quitter}</button>
      )}
    </div>
  );
}
